import React, { useState, useEffect, useRef, useCallback } from 'react';
import { gestureUnlockStyle } from './gesture-unlock.style';
import clsx from 'clsx';

type Dot = {
  id: number;
  x: number;
  y: number;
};

interface GestureUnlockProps {
	type: 'setting' | 'login';
	spacing?: number;
	isDisable?: boolean;
	checkIsPatternValid: (selectedPattern: number[]) => Promise<boolean>; 
	onPatternComplete?: (pattern: number[]) => void;
	onLoadingChange?: (isLoading: boolean) => void; 
}

const gridSize = 3;
const successTime = 1000;
const warningTime = 1000;
const middleDotMap: {[key in string]: number} = {
	'1-3': 2, 
	'1-7': 4, 
	'1-9': 5, 
	'2-8': 5, 
	'3-7': 5, 
	'3-9': 6,
	'4-6': 5, 
	'7-9': 8
};

const GestureUnlock: React.FC<GestureUnlockProps> = ({
	type,
	spacing = 80,
	isDisable,
	checkIsPatternValid,
	onPatternComplete,
	onLoadingChange,
}) => {
	const [selectedDots, setSelectedDots] = useState<Dot[]>([]);
	const [isUnlocking, setIsUnlocking] = useState<boolean>(false);
	const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null);
	const gridRef = useRef<HTMLDivElement>(null);
	const [dots, setDots] = useState<Dot[]>([]);
	const [isSuccess, setIsSuccess] = useState(false);
	const [isWarning, setIsWarning] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const isActive = !isSuccess && !isWarning && !isLoading && !isDisable;
	const selectedDotsRef = useRef<Dot[]>(selectedDots);
	useEffect(() => {
		selectedDotsRef.current = selectedDots;
	}, [selectedDots]);

	useEffect(() => {
		onLoadingChange && onLoadingChange(isLoading);
	}, [isLoading, onLoadingChange]);
	
	useEffect(() => {
		const generateDots = () => {
			const newDots: Dot[] = [];
			for (let row = 0; row < gridSize; row++) {
				for (let col = 0; col < gridSize; col++) {
					newDots.push({
						id: row * gridSize + col + 1,
						x: col * spacing,
						y: row * spacing,
					});
				}
			}
			return newDots;
		};
		setDots(generateDots());
	}, []);
	
	const handleStart = (dot: Dot) => {
		if (isActive) {
			setIsUnlocking(true);
			setSelectedDots([dot]);
			setCursorPosition(null);
		}
	};
	
	const handleMouseMove = (e: MouseEvent) => {
		if (isActive && isUnlocking && gridRef.current) {
			const gridRect = gridRef.current.getBoundingClientRect();
			const clientX = e.clientX;
			const clientY = e.clientY;
	
			setCursorPosition({
				x: clientX - gridRect.left,
				y: clientY - gridRect.top,
			});
		}
	};
	
	const handleEnter = useCallback((dot: Dot) => {
		if (isActive && isUnlocking && !selectedDots.some((d) => d.id === dot.id)) {
			const lastSelected = selectedDots[selectedDots.length - 1];
			const middleDotKey = `${Math.min(lastSelected.id, dot.id)}-${Math.max(lastSelected.id, dot.id)}`;
			const middleDotId = middleDotMap[middleDotKey];
	
			if (middleDotId && !selectedDots.some((d) => d.id === middleDotId)) {
				const middleDot = dots.find((d) => d.id === middleDotId);
				if (middleDot) {
					setSelectedDots((prevSelectedDots) => [...prevSelectedDots, middleDot]);
				}
			}
			
			setSelectedDots((prevSelectedDots) => [...prevSelectedDots, dot]);
		}
	}, [dots, isActive, isUnlocking, selectedDots]);
	
	const handleEnd = () => {
		if (isActive && isUnlocking) {
			setIsUnlocking(false);
			checkUnlockPattern();
		}
	};
	
	const checkUnlockPattern = async () => {
		if (isActive) {
			try {
				setIsLoading(true);
				const selectedPattern = selectedDotsRef.current.map((dot) => dot.id);
				
				if (onPatternComplete) {
					onPatternComplete(selectedPattern);
				}
				const isValid = await checkIsPatternValid(selectedPattern);
				if (isValid) {
					if (type === 'login') {
						setSelectedDots([]);
					} else if (type==='setting') {
						setIsSuccess(true);
						setTimeout(() => {
							setSelectedDots([]);
							setIsSuccess(false);
						}, successTime);  
					}
				} else {
					throw new Error();
				}
			} catch (e) {
				setIsWarning(true);
				setTimeout(() => {
					setSelectedDots([]);
					setIsWarning(false);
				}, warningTime);
			} finally {
				setCursorPosition(null);
				setIsLoading(false);
			}
		}
	};

	//觸控沒有enter事件, 算出dot與觸控position之位置決定是否選擇
	const handleTouchMove = (e: React.TouchEvent) => {
		if (isActive && isUnlocking && gridRef.current) {
			const gridRect = gridRef.current.getBoundingClientRect();
			const clientX = e.touches[0].clientX;
			const clientY = e.touches[0].clientY;
		
			setCursorPosition({
				x: clientX - gridRect.left,
				y: clientY - gridRect.top,
			});
		
			const touchX = clientX - gridRect.left;
			const touchY = clientY - gridRect.top;
			dots.forEach((dot) => {
				const dotRadius = 25; // 半徑

				// 以dot中心
				const isTouchingDot =
				touchX >= dot.x &&
				touchX <= dot.x + dotRadius*2 &&
				touchY >= dot.y &&
				touchY <= dot.y + dotRadius*2;
		
				if (isTouchingDot && !selectedDots.some((d) => d.id === dot.id)) {
					handleEnter(dot);
				}
			});
		}
	};


	
	const generatePath = () => {
		if (selectedDots.length < 1) return '';
		const radius = 25;
		const path = selectedDots
			.map((dot, index) =>
				index === 0 ? `M ${dot.x + radius},${dot.y + radius}` : `L ${dot.x + radius},${dot.y + radius}`
			)
			.join(' ');
	
		if (isUnlocking && cursorPosition) {
			return `${path} L ${cursorPosition.x},${cursorPosition.y}`;
		}
	
		return path;
	};
	// mouse事件註冊在全域才能拖動至component之外
	useEffect(() => {
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleEnd);
		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleEnd);
		};
	}, [isUnlocking]);
	
	return (
		<div
			className={gestureUnlockStyle.gridStyle}
			ref={gridRef}
			onTouchMove={(e) => handleTouchMove(e)}// 註冊觸控移動事件
			onTouchEnd={() => handleEnd()}// 註冊觸控結束事件
			style={{
				touchAction: 'none'
			}}
		>
			<svg
				width="210"
				height="210"
				className={clsx(gestureUnlockStyle.svgLine, {
					warning: isWarning,
					success: isSuccess,
				})}
			>
				<path
					d={generatePath()}
					strokeWidth="12"
					strokeLinecap="round"
					strokeLinejoin="round"
					fill="none"
				/>
			</svg>
	
			{dots.map((dot) => (
				<div
					key={dot.id}
					onMouseDown={() => handleStart(dot)}// 紀錄滑鼠按下之dot
					onMouseEnter={() => handleEnter(dot)}// 紀錄滑鼠進入之dot
					onTouchStart={() => handleStart(dot)}// 紀錄手機點擊之dot
					style={{ top: `${dot.y}px`, left: `${dot.x}px`, position: 'absolute' }}
				>
					<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" fill="none">
						<circle
							cx="25"
							cy="25"
							r="24"
							strokeWidth="2"
							className={clsx(gestureUnlockStyle.circleStroke, {
								active: selectedDots.some((d) => d.id === dot.id),
								warning: selectedDots.some((d) => d.id === dot.id) && isWarning,
								success: selectedDots.some((d) => d.id === dot.id) && isSuccess,
							})}
						/>
						<circle
							cx="25"
							cy="25"
							r="6"
							className={clsx(gestureUnlockStyle.circleDot, {
								active: selectedDots.some((d) => d.id === dot.id),
								warning: selectedDots.some((d) => d.id === dot.id) && isWarning,
								success: selectedDots.some((d) => d.id === dot.id) && isSuccess,
							})}
						/>
					</svg>
				</div>
			))}
		</div>
	);
};
  

export default GestureUnlock;


